import {useContext, useState} from 'react';

import {ErrorMessage} from 'formik';
import {useTranslation} from 'react-i18next';

import {Box, Grid, TextField, Typography, useTheme} from '@mui/material';

import {HardFactsFieldsLanguageSkillsDialog} from './HardFactsFieldsLanguageSkillsDialog';
import comment_icon from '../../../../../../../../../assets/job_postings/icons/comment-icon.png';
import {EmpionAddButtonRounded} from '../../../../../../../../../components/EmpionAddButtonRounded/EmpionAddButtonRounded';
import {LanguageContext} from '../../../../../../../../../contexts/LanguageContext/LanguageContext';
import {getFieldStyle} from '../../../../../../../../../layouts/theme/FieldStyles';
import {SUPPORTED_LANGUAGE_LEVELS} from '../../../../../../../../../lib/constants';
import {ALL_LANGUAGES_LIST, SupportedLanguageList} from '../../../../../../../../../lib/languagesListConst';
import {LanguageSkill} from '../../../../../types/JobPostingsTypes';

interface HardFactsFieldsLanguagesPropTypes {
  formik: any;
}

export const HardFactsFieldsLanguages = ({formik}: HardFactsFieldsLanguagesPropTypes) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const currentSystemLanguage = useContext(LanguageContext);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [languageSkillsDisplayText, setLanguageSkillsDisplayText] = useState(
    formik.values.languageSkills
      ? formik.values.languageSkills
        .map((languageSkill: LanguageSkill) => {
          const language = ALL_LANGUAGES_LIST.find((language) => language.id === languageSkill.language);
          const languageLevel = SUPPORTED_LANGUAGE_LEVELS.find(
            (languageLevel) => languageLevel === languageSkill.level
          );

          if (language && languageLevel) {
            return `${language.label[currentSystemLanguage]} ${languageLevel}`;
          } else {
            return '';
          }
        })
        .join(', ')
      : ''
  );

  const openLanguageSkillsDialog = () => {
    setDialogOpen(true);
  };

  const closeLanguageSkillsDialog = () => {
    setDialogOpen(false);
  };

  const updateLanguageSkills = (languageSkills: LanguageSkill[]) => {
    const localisedLanguageOptions = ALL_LANGUAGES_LIST.map((language: SupportedLanguageList) => ({
      id: language.id,
      label: language.label[currentSystemLanguage],
    }));

    setLanguageSkillsDisplayText(
      languageSkills
        .map((languageSkill) => {
          const language = localisedLanguageOptions.find((language) => language.id === languageSkill.language);
          const languageLevel = SUPPORTED_LANGUAGE_LEVELS.find(
            (languageLevel) => languageLevel === languageSkill.level
          );

          if (language && languageLevel) {
            return `${language.label} ${languageLevel}`;
          } else {
            return '';
          }
        })
        .join(', ')
    );
  };

  return (
    <Grid container display={'flex'} flexDirection={'row'}>
      <Grid item xs={12} display={'flex'} gap={2}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            '& .MuiGrid-item': {
              padding: '0px',
            },
          }}
        >
          <Box component='img' src={comment_icon} alt='comment_icon' />
          <TextField
            type='text'
            name='languages'
            label={`${t('companyJobPostings.newJobDialog.formLabels.languages')}*`}
            size='small'
            fullWidth
            aria-readonly={true}
            value={languageSkillsDisplayText}
            sx={getFieldStyle(theme)}
          />
          <EmpionAddButtonRounded onClick={openLanguageSkillsDialog} />
          <HardFactsFieldsLanguageSkillsDialog
            formik={formik}
            dialogOpen={dialogOpen}
            onUpdate={updateLanguageSkills}
            onClose={closeLanguageSkillsDialog}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box ml={3}>
          <Typography variant='body2' color='error'>
            <ErrorMessage name='languageSkills' component='span' />
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};
